import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, BookOpen, LogOut, Clock, Quote, Settings as SettingsIcon } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { analytics } from "@/lib/analytics";
import { Footer } from "@/components/Footer";

export default function Choose() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [domains, setDomains] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/domains").then(({ data }) => setDomains(data)).catch(() => {});
  }, []);

  const start = async (mode, domain) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/conversations", { mode, domain });
      analytics.conversationStarted({ mode, domain });
      navigate(`/chat/${data.id}`);
    } catch (err) {
      if (err.response?.status === 402) {
        // Free reflection already complete — invite to membership.
        navigate("/pricing");
        return;
      }
      toast(err.response?.data?.detail || "Could not open a session.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 px-6 sm:px-10 py-8">
      <header className="flex items-center justify-between max-w-4xl mx-auto">
        <span className="text-xs tracking-[0.35em] uppercase text-[var(--mirror-text-2)]">
          Mirror
        </span>
        <div className="flex items-center gap-6">
          <button
            data-testid="nav-history-button"
            onClick={() => navigate("/history")}
            className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
          >
            <Clock size={14} /> Chats
          </button>
          <button
            data-testid="nav-journey-button"
            onClick={() => navigate("/journey")}
            className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
          >
            <BookOpen size={14} /> Journey
          </button>
          <button
            data-testid="nav-reviews-button"
            onClick={() => navigate("/reviews")}
            className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
          >
            <Quote size={14} /> Reviews
          </button>
          <button
            data-testid="nav-settings-button"
            onClick={() => navigate("/settings")}
            className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
          >
            <SettingsIcon size={14} /> Settings
          </button>
          <button
            data-testid="nav-logout-button"
            onClick={logout}
            className="text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
            aria-label="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto mt-20 sm:mt-28">
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-center"
        >
          Where shall we look?
        </motion.h1>

        <motion.button
          data-testid="start-open-conversation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          onClick={() => start("open", null)}
          disabled={busy}
          className="mt-14 w-full text-left rounded-2xl border border-[rgba(242,236,228,0.1)] hover:border-[rgba(242,236,228,0.25)] p-8 transition-colors duration-700 group disabled:opacity-40"
        >
          <div className="flex items-center gap-3 text-[var(--mirror-accent)]">
            <MessageCircle size={18} />
            <span className="text-xs tracking-[0.25em] uppercase">Open Conversation</span>
          </div>
          <p className="mt-4 font-serif-display text-2xl font-light text-[var(--mirror-text)]">
            Begin anywhere. Follow the thread.
          </p>
          <p className="mt-2 text-sm text-[var(--mirror-text-3)]">
            No map. Just what is on your mind.
          </p>
        </motion.button>

        <div className="mt-16">
          <p className="text-center text-xs tracking-[0.25em] uppercase text-[var(--mirror-text-3)]">
            Or follow a guided flow
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {domains.map((d, i) => (
              <motion.button
                key={d.key}
                data-testid={`domain-card-${d.key}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 * i }}
                onClick={() => start("guided", d.key)}
                disabled={busy}
                className="text-left rounded-2xl border border-[rgba(242,236,228,0.06)] hover:border-[rgba(242,236,228,0.22)] p-6 transition-colors duration-700 disabled:opacity-40"
              >
                <h3 className="font-serif-display text-xl font-light text-[var(--mirror-text)]">
                  {d.title}
                </h3>
                <p className="mt-2 text-sm text-[var(--mirror-text-3)] leading-relaxed">
                  {d.subtitle}
                </p>
              </motion.button>
            ))}
          </div>
        </div>
      </main>
      </div>
      <Footer />
    </div>
  );
}
